import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, FileText, ShieldCheck } from "lucide-react";
import { api } from "@/app/api";
import { Card, StatusBadge } from "@/app/ui";
import { formatDate, statusLabel } from "@/lib/opportunities";

const deadlineApproaching = (value) => {
  if (!value) return false;
  const diff = new Date(`${value}T23:59:59`) - new Date();
  return diff >= 0 && diff <= 21 * 24 * 60 * 60 * 1000;
};

export default function Applications() {
  const [apps, setApps] = useState(null);

  useEffect(() => {
    api.get("/individual/applications")
      .then((response) => setApps(response.data.applications))
      .catch(() => setApps([]));
  }, []);

  return (
    <div data-testid="applications-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-ink">Application centre</h1>
          <p className="mt-2 max-w-2xl text-slatey">Track drafts, submitted applications, readiness, references and next actions in one place.</p>
        </div>
        <Link to="/app/discover" data-testid="applications-discover-link" className="rounded-full bg-cobalt px-4 py-2.5 text-sm font-bold text-white">
          Discover opportunities
        </Link>
      </div>

      <p className="mt-6 text-sm font-semibold text-slatey" data-testid="applications-count">
        {apps ? `${apps.length} application${apps.length === 1 ? "" : "s"}` : "Loading applications…"}
      </p>

      <div className="mt-4 space-y-4">
        {apps?.map((application) => (
          <Card key={application.id} data-testid={`application-${application.id}`} className="p-5 sm:p-6">
            <div className="grid gap-5 lg:grid-cols-[1fr_240px] lg:items-start">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge status={application.status} />
                  {application.is_demo && <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slatey">DEMO/SEEDED DATA</span>}
                  {deadlineApproaching(application.application_deadline) && ["draft", "preparing", "ready_to_review"].includes(application.status) && (
                    <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-bold text-amber-700">Deadline approaching</span>
                  )}
                </div>
                <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-ink">{application.opportunity_title}</h2>
                <p className="mt-1 text-sm text-slatey">{application.provider}</p>
                <div className="mt-4 grid gap-3 text-sm text-slatey sm:grid-cols-2">
                  <p className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-cobalt" /> {application.intake_label} · starts {formatDate(application.start_date)}</p>
                  <p className="flex items-center gap-2"><FileText className="h-4 w-4 text-cobalt" /> Reference <span className="font-bold text-ink">{application.application_reference}</span></p>
                </div>
                <p className="mt-4 rounded-2xl bg-surface p-3 text-sm font-semibold text-ink" data-testid={`next-action-${application.id}`}>{application.next_action}</p>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-surface p-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-bold text-slatey">Progress</span>
                  <span className="font-bold text-ink">{application.progress}%</span>
                </div>
                <div className="mt-3 h-2.5 rounded-full bg-slate-200">
                  <div className="h-2.5 rounded-full bg-cobalt transition-all" style={{ width: `${application.progress}%` }} />
                </div>
                <p className="mt-3 text-xs capitalize text-slatey">{statusLabel(application.status)}</p>
                <p className="mt-1 text-xs text-slatey">Updated {formatDate((application.updated_at || "").slice(0, 10))}</p>
                <Link to={`/app/applications/${application.id}`} data-testid={`view-application-${application.id}`}
                  className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-cobalt px-4 py-2.5 text-sm font-bold text-white">
                  Open application <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {apps?.length === 0 && (
        <Card data-testid="applications-empty-state" className="mt-6 text-center">
          <ShieldCheck className="mx-auto h-10 w-10 text-cobalt" />
          <h2 className="mt-4 font-display text-2xl font-bold text-ink">No applications yet</h2>
          <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-slatey">Choose an opportunity, select an intake and OpenDegree will create a reusable draft application.</p>
          <Link to="/app/discover" className="mt-5 inline-flex rounded-full bg-cobalt px-5 py-3 text-sm font-bold text-white">Discover opportunities</Link>
        </Card>
      )}
    </div>
  );
}
